import { createContext, useContext, useMemo, useState } from 'react';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import {
  useVote,
  useWidget,
  VoteAPIResponseCode,
} from '@telescope/cassini-hooks';
import { Open, Closed } from 'generated-types/widget';

type AvailablePrizes = boolean | null;

type PrizeContextValue = {
  hasAvailablePrize: AvailablePrizes,
  refetchAvailablePrizes: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<AvailablePrizes, Error>>;
}

const PrizeContext = createContext<PrizeContextValue | undefined>(undefined);

export interface PrizeProviderProps {
  children: React.ReactNode;
}

export const AvailablePrizeProvider = ({ children } : PrizeProviderProps) => {
  const { data: widget } = useWidget({ select: (data: Open | Closed) => data });
  const { sid } = widget!;
  const { data: settings } = useWidget({ select: (data: Open) =>  data.widget.global_views.auth.settings });
  const { isUnavailablePrizesMessageEnabled } = settings!
  const vote = useVote();

  const checkAvailablePrizes = async (): Promise<boolean | null> => {
    if (!isUnavailablePrizesMessageEnabled) {
      return null;
    }

    const { response_code, available_seed_count } = await vote({
      action_type: 'check_prizes'
    });

    if (response_code !== VoteAPIResponseCode.VALID) {
      return null;
    }

    return parseInt(available_seed_count, 10) > 0
  }

  const {
    data: hasAvailablePrize,
    refetch,
  } = useQuery<any, Error>({
    queryKey: ['available-prizes', { sid }],
    queryFn: checkAvailablePrizes,
    enabled: false,
    staleTime: Infinity
  });

  const value = useMemo(() => ({
    hasAvailablePrize,
    refetchAvailablePrizes: refetch
  }), [
    hasAvailablePrize,
    refetch
  ])

  return <PrizeContext.Provider value={value}>{children}</PrizeContext.Provider>
}

export function usePrizes() {
  const context = useContext(PrizeContext);

  if (context === undefined) {
    throw new Error('usePrizes must be used within a PrizeContext');
  }

  return context;
}